import { MEMBERSHIP_COST } from 'constants/plusMembership';
import useBrandLinks from 'hooks/useBrandLinks';
import { formatDate } from 'utils/dateUtils';
import { getDecimalDisplay } from 'utils/priceUtils';

function TermsAndConditionsLink({ children }) {
    const { terms } = useBrandLinks();

    return (
        <a className="underline" href={terms} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    );
}

/**
 * Renders the join terms and conditions.
 * @param {Object} props
 * @param {number} props.annualPlusMembershipCost - The annual plus membership cost.
 * @param {Object} props.availableCredits - The available credits.
 * @param {number} props.availableCredits.credits - The available credits.
 * @param {number} props.availableCredits.creditsExpirationDays - The available credits expiration days.
 * @param {Object} props.availableTrialMembership - The available trial membership.
 * @param {number} props.availableTrialMembership.trialDurationDays - The available trial membership duration days.
 * @param {string} props.brand - The brand.
 * @param {string} props.ctaCopy - The cta copy.
 * @param {boolean} props.earlyRenewal - Whether the early renewal is enabled.
 * @param {Object} props.membershipRenewalDate - The membership renewal date.
 * @param {string} props.membershipRenewalDate.date - The membership renewal date.
 * @param {Object} props.newMemberOffer - The new member offer.
 * @param {string} props.newMemberOffer.type - The new member offer type.
 * @param {number} props.newMemberOffer.discountAmount - The new member offer discount amount.
 * @param {string} props.newMemberOffer.discountType - The new member offer discount type.
 * @param {number} props.newMemberOffer.discountPrice - The new member offer discount price.
 * @param {string} props.plusMembershipBranding - The plus membership branding.
 */
function JoinTermsAndConditions({
    annualPlusMembershipCost,
    availableCredits,
    availableTrialMembership,
    brand,
    ctaCopy,
    earlyRenewal,
    membershipRenewalDate,
    newMemberOffer,
    plusMembershipBranding,
}) {
    // EARLY RENEWAL TERMS (for Paid memberships)
    if (earlyRenewal) {
        return (
            <div className="text-compact">
                <p>
                    By clicking “{ctaCopy},” you agree to renew your annual {plusMembershipBranding} membership and
                    authorize {brand} to charge the discounted membership fee above (plus tax) to your account’s payment
                    method today. Discounted fee only applies to upcoming year; future renewals will be charged at the
                    regular ${getDecimalDisplay(MEMBERSHIP_COST)} rate until you cancel.{' '}
                    <TermsAndConditionsLink>Cancel</TermsAndConditionsLink> in account to avoid renewal charges.{' '}
                    {plusMembershipBranding} subject to the{' '}
                    <TermsAndConditionsLink>{plusMembershipBranding} Terms</TermsAndConditionsLink>.
                </p>
            </div>
        );
    }

    // TRIAL TERMS (Discounts and no offer states)
    if (availableTrialMembership && !availableCredits?.credits) {
        if (newMemberOffer?.type === 'discount') {
            return (
                <p className="text-compact text-grey">
                    By starting your {plusMembershipBranding} free trial, you agree to the{' '}
                    <TermsAndConditionsLink>{plusMembershipBranding} Terms</TermsAndConditionsLink> and authorize{' '}
                    {brand} to charge an annual membership fee (plus applic. tax) to your account&apos;s payment method
                    beginning on {formatDate(membershipRenewalDate?.date, true)}. Discounted membership fee of $
                    {getDecimalDisplay(newMemberOffer.discountPrice)} only applies to first year; future renewals will
                    be charged the regular rate of ${getDecimalDisplay(annualPlusMembershipCost)} (plus applic. tax)
                    until you cancel. <TermsAndConditionsLink>Cancel</TermsAndConditionsLink> in account to avoid
                    renewal charges.
                </p>
            );
        }

        return (
            <div className="text-compact text-grey">
                <p>
                    By starting your {plusMembershipBranding} free trial, you agree to the{' '}
                    <TermsAndConditionsLink>{plusMembershipBranding} Terms</TermsAndConditionsLink> and authorize{' '}
                    {brand} to charge an annual ${getDecimalDisplay(annualPlusMembershipCost)} membership fee (plus
                    applic. tax) to your account’s payment method beginning on{' '}
                    {formatDate(membershipRenewalDate?.date, true)} until you cancel.{' '}
                    <TermsAndConditionsLink>Cancel</TermsAndConditionsLink> in account to avoid renewal charges.
                </p>
            </div>
        );
    }

    // CREDITS TERMS (for Trial & Paid memberships)
    if (availableCredits?.credits > 0) {
        return (
            <div className="text-compact text-grey">
                <p>
                    By joining {plusMembershipBranding}, you agree to the {plusMembershipBranding} Terms and authorize{' '}
                    {plusMembershipBranding} to charge an annual ${getDecimalDisplay(annualPlusMembershipCost)}{' '}
                    membership fee (plus applic. tax) to your account&apos;s payment method until you cancel. Cancel in
                    account to avoid renewal charges. Offer includes ${availableCredits.credits} account credit applied
                    to your account upon enrollment. Credit must be used for an order charged within{' '}
                    {availableCredits.creditsExpirationDays} days of enrollment. Offer subject to applicable order
                    minimum. Exclusions may apply.
                </p>
            </div>
        );
    }

    // DISCOUNT TERMS (for Paid memberships)
    if (newMemberOffer?.type === 'discount') {
        return (
            <p className="text-compact text-grey">
                By joining {plusMembershipBranding}, you agree to the{' '}
                <TermsAndConditionsLink>{plusMembershipBranding} Terms</TermsAndConditionsLink> and authorize {brand} to
                charge an annual membership fee (plus applic. tax) to your account&apos;s payment method today.
                Discounted membership fee of ${getDecimalDisplay(newMemberOffer.discountPrice)} only applies to first
                year; future renewals will be charged the regular rate of ${getDecimalDisplay(annualPlusMembershipCost)}{' '}
                (plus applic. tax) until you cancel. <TermsAndConditionsLink>Cancel</TermsAndConditionsLink> in account
                to avoid renewal charges.
            </p>
        );
    }

    // DEFAULT TERMS (for Paid memberships)
    return (
        <div className="text-compact text-grey">
            <p>
                By joining {plusMembershipBranding}, you agree to the{' '}
                <TermsAndConditionsLink>{plusMembershipBranding} Terms</TermsAndConditionsLink> and authorize {brand} to
                charge an annual ${getDecimalDisplay(annualPlusMembershipCost)} membership fee (plus applic. tax) to
                your account’s payment method until you cancel. <TermsAndConditionsLink>Cancel</TermsAndConditionsLink>{' '}
                in account to avoid renewal charges.
            </p>
        </div>
    );
}

export { JoinTermsAndConditions };
